import { queryOptions, type Query, type QueryClient } from "@tanstack/react-query";
import type { MyWorkService } from "@/services/my-work.service";
import type { TMyWorkAvailability, TMyWorkMention, TMyWorkRow } from "@/types/my-work";
import { rereadAfterWrite } from "./invalidation";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * My Work (NG-2806 PR 11), under one `my-work` prefix:
 *
 * - **The unified feed**, `[slug, uid, 'my-work', 'feed', {order_by}]`: the
 *   caller's assigned tasks and events and owned requests, one page of up to
 *   100 rows sorted by the server (NG-493). The table and the Assigned-to-me
 *   card read the same entry.
 * - **The week's availability**, `[slug, uid, 'my-work', 'availability']`: the
 *   Availability card.
 * - **The mentions**, `[slug, uid, 'my-work', 'mentions', {unread}]`: the
 *   unread ones for the card (narrowed by the server, since the feed is capped,
 *   NG-651) and the full history for the View-all drawer, read on its first
 *   open.
 *
 * All three are **mutable** (contract §2): another person assigns work,
 * books time or mentions the caller at any time.
 *
 * @writers A review decision taken from the table re-reads the feed
 * (`invalidateMyWorkFeed`), so the decided request leaves it. A task, event or
 * request written anywhere else in the tab re-reads the feed and the
 * availability (`myWorkChanged`), called from the work-item and request write
 * helpers, `IntakeStore`'s review decisions and the Events page's own writes
 * (NG-3586). Marking a
 * mention read patches both mention lists (`patchMyWorkMention`) before the
 * mark-read call, and puts them back if it fails; a rise in the notification
 * store's unread mention count re-reads the unread list.
 */

/** The feed's one page: every row the caller has, since the table filters in the browser. */
const MY_WORK_FEED_PAGE_SIZE = 100;

function myWorkFeedKey(scope: QueryScope, orderBy: string | undefined) {
  return scopedKey(scope, "my-work", "feed", { order_by: orderBy ?? "" });
}

/** Every cached feed in `scope`, whatever its sort. */
export function myWorkFeedPrefix(scope: QueryScope) {
  return scopedKey(scope, "my-work", "feed");
}

function myWorkMentionsKey(scope: QueryScope, unread: boolean) {
  return scopedKey(scope, "my-work", "mentions", { unread });
}

/** Both mention lists in `scope`. */
export function myWorkMentionsKeyPrefix(scope: QueryScope) {
  return scopedKey(scope, "my-work", "mentions");
}

/** The unified feed, sorted by `orderBy` (a `myWorkOrderBy` token; none is the server's default). */
export function myWorkFeedQuery(
  service: Pick<MyWorkService, "fetchMyWork">,
  scope: QueryScope,
  orderBy: string | undefined,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: myWorkFeedKey(scope, orderBy),
    queryFn: scopedRead<TMyWorkRow[]>(
      async (request) =>
        (
          await service.fetchMyWork(
            scope.tenant,
            { per_page: MY_WORK_FEED_PAGE_SIZE, order_by: orderBy },
            request,
          )
        ).results,
      { retry: "safe-read" },
    ),
  });
}

/** The caller's booked and free time for the week. */
export function myWorkAvailabilityQuery(
  service: Pick<MyWorkService, "fetchMyWorkAvailability">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: scopedKey(scope, "my-work", "availability"),
    queryFn: scopedRead<TMyWorkAvailability>(
      (request) => service.fetchMyWorkAvailability(scope.tenant, request),
      { retry: "safe-read" },
    ),
  });
}

/** The caller's mentions: only the unread ones, or the full history. */
export function myWorkMentionsQuery(
  service: Pick<MyWorkService, "fetchMyWorkMentions">,
  scope: QueryScope,
  unread: boolean,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: myWorkMentionsKey(scope, unread),
    queryFn: scopedRead<TMyWorkMention[]>(
      async (request) =>
        (await service.fetchMyWorkMentions(scope.tenant, unread ? { unread: true } : {}, request))
          .results,
      { retry: "safe-read" },
    ),
  });
}

/** Re-read every cached feed in `scope`: a decision moved a row off it. */
export function invalidateMyWorkFeed(client: QueryClient, scope: QueryScope): Promise<void> {
  return client.invalidateQueries({ queryKey: myWorkFeedPrefix(scope) });
}

/**
 * After a confirmed write to a task, event or request in this tab: every
 * cached My Work feed and availability of `tenant`, under any principal, is
 * marked stale and the ones on screen re-read (NG-3586). Each writer knows its
 * own domain's lists, and My Work is read under its own key, so without this a
 * return to My Work inside the 60 s reuse window showed the row as it was
 * before the edit. The mentions are left alone: no such write changes them.
 * Through `rereadAfterWrite`, so a read in flight during the write cannot land
 * its older answer as fresh. Never rejects; a failed re-read is logged.
 */
export function myWorkChanged(client: QueryClient, tenant: string): void {
  const isWorkRead = (query: Query) =>
    query.queryKey[0] === tenant &&
    query.queryKey[2] === "my-work" &&
    (query.queryKey[3] === "feed" || query.queryKey[3] === "availability");
  rereadAfterWrite(client, isWorkRead).catch((error: unknown) => {
    console.warn("[my-work] re-reading after a write failed", { tenant, error });
  });
}

/** Re-read the unread mentions: one arrived that the cached list cannot hold. */
export function invalidateUnreadMentions(client: QueryClient, scope: QueryScope): Promise<void> {
  return client.invalidateQueries({ queryKey: myWorkMentionsKey(scope, true), exact: true });
}

/**
 * Set one mention's read state in both cached lists, so the card and the
 * drawer agree. The row is flipped in place rather than dropped: removing it
 * from the card would take it out from under the cursor mid-click.
 */
export function patchMyWorkMention(
  client: QueryClient,
  scope: QueryScope,
  mentionId: string,
  isUnread: boolean,
): void {
  client.setQueriesData<TMyWorkMention[]>({ queryKey: myWorkMentionsKeyPrefix(scope) }, (rows) =>
    rows?.map((m) => (m.id === mentionId ? { ...m, is_unread: isUnread } : m)),
  );
}
